import { Button, Card, Col, Row, Select, Skeleton } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CheckOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { subscriptionPlans } from "../../config";
import { calculatePricing } from "../../actionCreator/paymentActionCreator";
import { formatCurrency } from "../../utils/formatCurrency";

const PaymentSummary = ({ setStep, purchaseSubscription, loading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const cycle = searchParams.get("cycle") || "MONTHLY";
  const currency = searchParams.get("currency") || "INR";
  const dispatch = useDispatch();
  const plans = useSelector((state) => state.CommonReducer.subscriptions);
  const pricingDetails = useSelector(
    (state) => state.paymentReducer.pricingDetails,
  );
  const loadingPricing = useSelector(
    (state) => state.paymentReducer.pricingLoading,
  );
  const [selectedPlans, setSelectedPlans] = useState();
  const formatAmount = (value) => formatCurrency(value ?? 0, currency);
  const appliedDiscountAmount =
    Number(pricingDetails?.promoDiscount) > 0
      ? Number(pricingDetails?.promoDiscount)
      : Number(pricingDetails?.yearlyDiscount) > 0
        ? Number(pricingDetails?.yearlyDiscount)
        : 0;
  const appliedDiscountLabel =
    Number(pricingDetails?.promoDiscount) > 0
      ? "Sale Discount"
      : "Yearly Discount";

  useEffect(() => {
    if (plan) {
      setSelectedPlans(
        plans?.find((d) => String(d?.id) === String(plan)),
      );
    }
  }, [plan, plans]);

  useEffect(() => {
    if (selectedPlans?.id) {
      dispatch(
        calculatePricing({
          packageId: selectedPlans.id,
          billingCycle: cycle.toUpperCase(),
          currency: currency,
        }),
      );
    }
  }, [dispatch, selectedPlans, cycle, currency]);

  return (
    <>
      <Row gutter={[20, 20]} className="mt20" style={{ width: 1000 }}>
        <Col xs={24} md={14}>
          <Card title="Overview">
            <div>
              <div className="fw-semibold heading13px mb10">YOUR PLAN</div>
              <Select
                value={selectedPlans?.id}
                placeholder="Select Plan"
                options={plans
                  ?.filter((d) => d?.name !== subscriptionPlans?.trail)
                  ?.map((i) => ({ label: i?.name, value: i?.id }))}
                onChange={(val) => {
                  setSelectedPlans(plans?.find((d) => d?.id === val));
                  searchParams.set("plan", String(val));
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              />
              <div className="fw-bold mt20 d-flex align-items-center justify-content-between heading18px">
                1 User{" "}
                <span>
                  {pricingDetails &&
                    pricingDetails.baseCost > pricingDetails.preTaxTotal && (
                      <span
                        style={{
                          textDecoration: "line-through",
                          color: "#999",
                          fontSize: "14px",
                          marginRight: "8px",
                        }}
                      >
                        {formatAmount(pricingDetails.baseCost)}
                      </span>
                    )}{" "}
                  <span className="fw-semibold">
                    {formatAmount(pricingDetails?.preTaxTotal)}
                  </span>
                </span>
              </div>
              <div className="text-color-34C38F fw-semibold heading18px">
                <span className="fw-semibold">
                  {formatAmount(pricingDetails?.finalPayableAmount)}
                </span>{" "}
                Until Next Billing Date
              </div>
              <div className="fw-bold heading18px mt30 mb10">Plan Detail</div>
              <div className="d-flex align-items-center mb5 justify-content-between">
                <span className="d-flex gap-10">
                  <CheckOutlined className="text-color-34C38F fw-semibold" />
                  Email credits{" "}
                </span>
                <span>{selectedPlans?.emailSentCountLimit}/months</span>
              </div>
              <div className="d-flex align-items-center mb5 justify-content-between">
                <span className="d-flex gap-10">
                  <CheckOutlined className="text-color-34C38F fw-semibold" />
                  Lead creation credits{" "}
                </span>
                <span>{selectedPlans?.leadCreationLimit}/months</span>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card title="Order Summary" className="order-summary-card">
            <Skeleton
              loading={loadingPricing}
              active
              title={false}
              paragraph={{ rows: 5 }}
            >
              <div className="px-20 d-flex align-items-center justify-content-between mb10">
                Base Plan Price{" "}
                <span>
                  <span className="fw-semibold">
                    {formatAmount(pricingDetails?.baseCost)}
                  </span>
                </span>
              </div>

              {pricingDetails && appliedDiscountAmount > 0 && (
                <div className="px-20 d-flex align-items-center justify-content-between mb10 text-color-34C38F">
                  <div className="d-flex flex-column">
                    <span>{appliedDiscountLabel}</span>
                    {Number(pricingDetails?.promoDiscount) > 0 &&
                      pricingDetails.appliedOfferName && (
                      <span
                        className="fs-12 fw-normal"
                        style={{ color: "#999" }}
                      >
                        (Code: {pricingDetails.appliedOfferName})
                      </span>
                    )}
                  </div>
                  <span>
                    -<span className="fw-semibold">
                      {formatAmount(appliedDiscountAmount)}
                    </span>
                  </span>
                </div>
              )}

              <div className="px-20 d-flex align-items-center justify-content-between mb10 fw-medium">
                Subtotal{" "}
                <span>
                  <span className="fw-semibold">
                    {formatAmount(pricingDetails?.preTaxTotal)}
                  </span>
                </span>
              </div>

              <div className="px-20 d-flex align-items-center justify-content-between">
                Tax (IGST) @18%{" "}
                <span>
                  <span className="fw-semibold">
                    {formatAmount(pricingDetails?.finalTax)}
                  </span>
                </span>
              </div>
              <div className="fw-medium mt10 py-10 px-20 bg-F5F7FF d-flex align-items-center justify-content-between">
                Total{" "}
                <span>
                  <span className="fw-semibold">
                    {formatAmount(pricingDetails?.finalPayableAmount)}
                  </span>
                </span>
              </div>
            </Skeleton>
            <div className="px-20">
              <Button
                type="primary"
                className="theme-btn mt30"
                block
                loading={loading}
                onClick={() => {
                  purchaseSubscription(
                    {
                      packageName: selectedPlans?.name,
                      billingCycle: cycle.toUpperCase(),
                      currency: currency,
                    },
                    () => setStep(3),
                  );
                }}
              >
                Buy Now
                <i className="fas fs-12 fa-arrow-right"></i>
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
      <div
        style={{ width: 1000 }}
        className="d-flex justify-content-between align-items-center"
      >
        <Button
          type="text"
          className="text-theme fw-medium"
          onClick={() => {
            searchParams.delete("payment");
            navigate(`${location.pathname}?${searchParams.toString()}`);
            setStep(1);
          }}
        >
          <span className="fw-medium heading18px d-flex gap-10">
            <ArrowLeftOutlined /> Back{" "}
          </span>
        </Button>
        <div></div>
      </div>
    </>
  );
};

export default PaymentSummary;
